import React, { useEffect, useMemo, useState } from 'react';
import { useStoreState, useStoreActions } from 'easy-peasy';
import { Box, Grid, IconButton, Tooltip } from '@material-ui/core';
import AddBoxIcon from '@material-ui/icons/AddBox';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import VisibilityIcon from '@material-ui/icons/Visibility';
import styles from './PAConfig.module.scss';
import EnhancedTable from '../../components/enhancedTable/enhancedTable';
import { PACustomFieldOption } from './types';
import AddEditPAConfig from './components/AddEditPAConfig';
import DeleteConfirmationDialog from '../../common/DeleteConfirmationDialog';
import { SingleSelectWithArrayFilter } from '../../components/enhancedTable/filters/SingleSelectWithArrayFilter';

interface PAConfigProps {
  orgId?: string;
}

const PAConfig: React.FC<PAConfigProps> = ({
  orgId: orgId,
}) => {
  const commonAction = useStoreActions((actions: any) => actions.common);
  const settingsAction = useStoreActions((actions: any) => actions.settings);
  const paConfigList: PACustomFieldOption[] = useStoreState((state: any) => state.settings.paCustomFieldList);
  const [openAddPAConfig, setOpenAddPAConfig] = useState<boolean>(false);
  const [editRowData, setEditRowData] = useState<object>({});
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState<boolean>(false);
  const [deleteRowData, setDeleteRowData] = useState<object>({});
  const [submitDeleteLoader, setSubmitDeleteLoader] = useState<boolean>(false);

  const handleEdit = (rowData: object) => {
    setOpenAddPAConfig(true);
    setEditRowData(rowData);
  };

  const handleDelete = (rowData: object) => {
    setDeleteRowData(rowData);
  };
  const deletePACustomField = (data: object) => {
    settingsAction.deletePaCustomField(data)
      .then(function (response: any) {
        commonAction.setSubmitSnackbar({
          status: true,
          messages: response?.data?.data?.message || `Recors deleted successfully`,
        });
      })
      .catch(function (error: any) {
        // Handle error        
        commonAction.setSubmitSnackbar({
          status: true,
          messages: `Error deleting pa custom field. Please try again.`,
          error: true,
        });
      })
      .finally(() => {
        setSubmitDeleteLoader(false);
        setOpenDeleteConfirmation(false);
      });
  };

  useEffect(() => {
    settingsAction.getPaCustomFieldList({ orgId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const columns = useMemo(
    () => [
      {
        Header: 'Type',
        accessor: 'field_key',
        sortType: 'caseInsensitive',
      },
      {
        Header: 'Label',
        accessor: 'field_label',
        sortType: 'caseInsensitive',
      },
      {
        Header: 'FHIR Mapping',
        accessor: '',
        Cell: ({ row }: any) => {
          const length = row?.original?.fhir_path?.length;
          return length ? `Total ${length} FHIR Mapping` : 'N/A';
        },
        disableFilters: true,
      },
      {
        Header: 'Check at Level',
        accessor: '',
        Cell: ({ row }: any) => {
          const checkATLevel = row?.original?.check_at_level;
          return checkATLevel?.join(', ') || 'N/A';
        },
        disableFilters: true,
      },
      {
        Header: 'Display',
        accessor: 'field_category',
        Cell: ({ row }: any) => {
          const categories = row?.original?.field_category || [];
          return categories?.map(
            (item: any) => item.charAt(0).toUpperCase() + item.slice(1)
          )?.join(', ') || 'N/A';
        },
        Filter: SingleSelectWithArrayFilter,
        filter: 'arrayIncludes',
      },
      {
        Header: '',
        id: 'actions',
        width: 20,
        Cell: ({ row }: any) => {
          const data = row.original;
          const isEditDisabled = data.field_category?.includes('standard') || false;

          return (
            <Box display="flex" justifyContent="flex-end">
              {isEditDisabled ? (
                <Tooltip title="View">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => handleEdit(data)}
                  >
                    <VisibilityIcon />
                  </IconButton>
                </Tooltip>
              ) : (
                <>
                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleEdit(data)}
                      disabled={isEditDisabled}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      color="secondary"
                      onClick={() => {
                        handleDelete(data);
                        setOpenDeleteConfirmation(true);
                      }}
                      disabled={isEditDisabled}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </Box>
          );
        },
      }
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [paConfigList],
  ); 
  
  return (
    <div className={styles.root}>
      <Grid item md={12} className='paCustomTable'>
        <EnhancedTable
          columns={columns}
          data={paConfigList}
          skipPageReset={false}
          paginationMode="client"
          pages={undefined}
          limit={undefined}
          currentPage={undefined}
          handleChangePage={undefined}
          handleChangeRowsPerPage={undefined}
          paginationDefauldSize={undefined}
          rowSelection={undefined}
          sortBy={undefined}
          paging={undefined}
          loader={undefined}
          toolbar={{
            customPadding: '0px 0px 0px 5px',
            show: true,
            title: 'PA Config',
            enableSearch: false,
            actionButtonGroup: () => (
              <Tooltip title="Add PA Config">
                <IconButton
                  color="primary"
                  aria-label="Add"
                  onClick={() => {
                    setEditRowData({});
                    setDeleteRowData({});
                    setOpenAddPAConfig(true);
                  }}
                >
                  <AddBoxIcon />
                </IconButton>
              </Tooltip>
            ),
          }}
        />
      </Grid>
      {/* Add/Update Popup */}
      {openAddPAConfig && (
        <AddEditPAConfig 
          openAddPAConfig={openAddPAConfig}
          setOpenAddPAConfig={setOpenAddPAConfig}
          editRowData={editRowData}
          orgId={orgId}
        />
      )}
      {/* Delete Confirmation Popup */}
      {openDeleteConfirmation && (
        <DeleteConfirmationDialog open={openDeleteConfirmation} opts={{
          title: 'Delete PA Custom Field',
          description: 'Are you sure you want to delete the PA Custom Field?',
          btnOkText: 'Yes',
          btnCancelText: 'No',
          onClose: (value) => {
            if (value === 'Yes') {
              setSubmitDeleteLoader(true);
              deletePACustomField(deleteRowData);
            } else {
              setOpenDeleteConfirmation(false);
            }
          },
          submitLoader: submitDeleteLoader,
        }} />
      )}
    </div>
  );
};

export default PAConfig;
